import useFindOrder from "@repo/shopify/hooks/useFindOrder";
import useOrderDetail from "@repo/shopify/hooks/useOrderDetail";
import useOrderInfo from "@repo/shopify/hooks/useOrderInfo";
import useOrderPayMethod from "@repo/shopify/hooks/useOrderPayMethod";
import useOrderStatus from "@repo/shopify/hooks/useOrderStatus";
import { formatOrderAddress } from "@repo/shopify/utils/address";
import { OrderStatusButtonType, OrderStatusTrans, getTrackingInfo } from "@repo/shopify/utils/order";
import dayjs from "dayjs";
import React, { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";

import Link from "@atoms/Link";
import Loading from "@atoms/Loading";

import Address from "@molecules/Address";
import OrderLineItem from "@molecules/OrderLineItem";

import OrderPaymentInfo from "@organisms/OrderPaymentInfo";
import OrderStatusButton from "@organisms/OrderStatusButton";

import { DEFAULT_TITLE } from "@const/meta";
import { URLS } from "@const/urls";

import useLanguage from "@hooks/useLanguage";

import { useCustomerStore } from "@store/customer";
import { useLayoutStore } from "@store/layout";

import { formatAutoDiscountTitle } from "@utils/discount";

export default function OrderDetail() {
  const navigate = useNavigate();
  const { handle } = useParams();

  const { customer, token } = useCustomerStore();
  const { setMeta, openAccount, showFooter } = useLayoutStore();

  const { lang } = useLanguage();
  const { t } = useTranslation();

  useEffect(() => {
    window.scrollTo(0, 0);
    showFooter(true);
    setMeta({ title: `Order Detail - ${DEFAULT_TITLE}` });
  }, []);

  useEffect(() => {
    if (!token?.accessToken) {
      setTimeout(() => {
        sessionStorage.setItem("loginAfterUrl", `${URLS.ORDER}${handle}`);
        openAccount(true);
      }, 300);
    }
  }, [token]);

  const { order, loading, orderNotFound } = useFindOrder({ orderId: handle, accessToken: token.accessToken });

  const { completeOrder, refundOrder, returnOrder, totalReturnShippingFee, orderInfo, isLoading } = useOrderDetail({
    lang,
    customer,
    handle,
    order,
    orderNotFound,
  });

  useEffect(() => {
    if (!(loading || isLoading) && !order?.id && !orderInfo) {
      navigate(URLS.NOT_FOUND, { replace: true });
    }
  }, [order, loading, isLoading, orderInfo]);

  // 결제 수단 정보
  const paymentInfo = useOrderPayMethod({ lang, order });

  const { activeButton, orderStatus } = useOrderStatus(order, lang === "ko");

  // 주문 정보
  const {
    shippingAmount,
    taxAmount,
    dutyAmount,
    totalAmount,
    subTotalAmount,
    refundAmount,
    codeDiscountAmount,
    autoDiscountAmount,
    codeDiscountTitle,
    autoDiscountTitle,
    shippingTitle,
  } = useOrderInfo(order, orderInfo);

  // 배송 송장 정보
  const trackingInfo = getTrackingInfo({ order, lang });

  const estimatedDeliveryRaw = order?.customAttributes.find((a) => a.key === "estimatedDelivery")?.value;
  const estimatedDeliveryDate = estimatedDeliveryRaw ? dayjs(estimatedDeliveryRaw).format("MMM DD, YYYY") : null;

  // 배송지 정보
  const shippingAddress = formatOrderAddress({ address: order?.shippingAddress, lang });
  const billingAddress = formatOrderAddress({ address: order?.billingAddress, lang });

  return (
    <>
      {!order || loading || isLoading ? (
        <Loading type="page" />
      ) : (
        <section className="pc:col-span-12 col-span-8 flex flex-col gap-2.5">
          <Link as="text" className="w-fit" href={URLS.ORDER_HISTORY}>
            ← Order History
          </Link>
          <section className="flex">
            <h2 className="text-title grow">{order.name || ""}</h2>
            {activeButton !== OrderStatusButtonType.NONE && handle ? (
              <OrderStatusButton buttonType={activeButton} lineItems={completeOrder} orderId={handle} />
            ) : null}
          </section>
          <section className="tnt-gap-x flex flex-col gap-y-5">
            <section className="pc:col-span-12 col-span-8 flex flex-col">
              <span>{dayjs(order?.processedAt).format("MMM DD, YYYY")}</span>
              <span>{orderStatus ? t(OrderStatusTrans[orderStatus]) : orderStatus}</span>
              {trackingInfo ? (
                <Link as="text" className="w-fit font-normal underline" href={trackingInfo.url}>
                  {trackingInfo.number}
                </Link>
              ) : null}
            </section>

            <section className="tnt-gap-x pc:col-span-12 pc:grid-cols-12 col-span-8 grid grid-cols-6 gap-y-10">
              <section className="pc:col-span-4 col-span-8">
                <div className="pc:grid-cols-4 grid grid-cols-6 gap-x-2 gap-y-5">
                  <div className="pc:col-span-2 col-span-3 flex flex-col gap-y-1">
                    <span className="uppercase">Shipping Method</span>
                    <span className="font-normal">{shippingTitle}</span>
                  </div>
                  <div className="pc:col-span-2 col-span-3 flex flex-col gap-y-1">
                    <span className="uppercase">Payment Detail</span>
                    <span className="font-normal">{paymentInfo}</span>
                  </div>

                  {/* Shipping Address */}
                  <div className="pc:col-span-2 col-span-3 flex flex-col gap-y-1">
                    <span className="uppercase">Shipping Address</span>
                    <Address {...shippingAddress} />
                  </div>

                  {/* Billing Address */}
                  <div className="pc:col-span-2 col-span-3 flex flex-col gap-y-1">
                    <span className="uppercase">Billing Address</span>
                    <Address {...billingAddress} />
                  </div>
                  {order.fulfillmentStatus !== "FULFILLED" && estimatedDeliveryDate ? (
                    <div className="pc:col-span-2 col-span-3 flex flex-col gap-y-1">
                      <span className="uppercase">Estimated Delivery Date</span>
                      <span className="font-normal">{estimatedDeliveryDate}</span>
                    </div>
                  ) : null}
                </div>
              </section>

              <section className="tnt-gap-x pc:col-start-7 col-span-8 grid grid-cols-8 gap-y-5">
                {/* 반품 내역 - (returnRequested, returnApproved) */}
                {returnOrder.length > 0 ? (
                  <div className="tnt-gap-x col-span-8 flex flex-col gap-y-5">
                    <span className="uppercase">Return Items</span>
                    <ul className="tnt-gap-x pc:gap-y-2 col-span-8 flex flex-col gap-y-5">
                      {returnOrder.map((node) => (
                        <OrderLineItem key={node.id} node={node} />
                      ))}
                    </ul>
                  </div>
                ) : null}

                {/* 주문 진행 내역 - (unfulfilled, fulfilled, onhold) */}
                {completeOrder.length > 0 ? (
                  <div className="tnt-gap-x col-span-8 flex flex-col gap-y-5">
                    <span className="uppercase">Line items</span>
                    <ul className="tnt-gap-x pc:gap-y-2 col-span-8 flex flex-col gap-y-5">
                      {completeOrder.map((node) => (
                        <OrderLineItem key={node.id} node={node} />
                      ))}
                    </ul>
                  </div>
                ) : null}

                {/* 환불 내역 - (canceled, refunded)*/}
                {refundOrder.length > 0 ? (
                  <div className="tnt-gap-x col-span-8 flex flex-col gap-y-5">
                    <span className="uppercase">Refunded Items</span>
                    <ul className="tnt-gap-x pc:gap-y-2 col-span-8 flex flex-col gap-y-5">
                      {refundOrder.map((node) => (
                        <OrderLineItem key={node.id} node={node} />
                      ))}
                    </ul>
                  </div>
                ) : null}

                <div className="pc:col-span-2 pc:col-start-7 col-span-3 col-start-4 flex flex-col gap-y-1">
                  <OrderPaymentInfo
                    autoDiscountAmount={autoDiscountAmount}
                    autoDiscountTitle={formatAutoDiscountTitle(autoDiscountTitle)}
                    codeDiscountAmount={codeDiscountAmount}
                    codeDiscountTitle={codeDiscountTitle}
                    dutyAmount={dutyAmount}
                    originalTotalAmount={subTotalAmount}
                    refundAmount={refundAmount}
                    returnShippingFee={totalReturnShippingFee ?? 0}
                    shippingFee={shippingAmount}
                    taxAmount={taxAmount}
                    totalAmount={totalAmount}
                  />
                </div>
              </section>
            </section>
          </section>
        </section>
      )}
    </>
  );
}
